import { useMemo, useState } from 'react'

import { CompanySwitcher } from '@/features/companies/components/CompanySwitcher'
import { useTaskStages } from '@/features/projects/api'
import { useCompleteTask, useDeleteTask, useUpdateTask } from '@/features/tasks/api'
import { useMoveTask } from '@/features/tasks/reorder'
import { AssigneePicker, TaskDetailSheet } from '@/features/tasks/components'
import { PageHeader } from '@/shared/components/common'
import { EmptyState, ErrorState, NoResultsState, SkeletonCards } from '@/shared/components/data'
import { Button, Segmented } from '@/shared/components/ui'
import type { SegmentedOption } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import type { Task } from '@/shared/types/domain'

import { useDueSoonBuckets } from './api'
import { HORIZONS, useDueSoonFilters, type Horizon } from './use-due-soon-filters'
import { groupTasks, type GroupBy } from './buckets'
import { BucketSection } from './components/BucketSection'
import { DueSoonGroup } from './components/DueSoonGroup'

export interface DueSoonViewProps {
  title: string
  description: string
  /**
   * Fija el responsable (es lo unico que distingue `/me/tasks` de `/due-soon`).
   * Cuando esta fijo, el selector de responsable no se muestra: seria mentira
   * que se puede cambiar.
   */
  lockedAssigneeId?: string | null
  defaultGroupBy?: GroupBy
}

/**
 * La vista de vencimientos.
 *
 * Buckets en orden fijo — Vencidas → Hoy → Esta semana → Próximas 2 semanas —
 * y adentro de cada uno, el agrupado por persona o por proyecto.
 *
 * **Los buckets son los del servidor, tal cual llegan**: clave, orden y
 * reparto. Acá no se vuelve a decidir en qué tramo cae una fecha; esa regla
 * vive en un solo lugar y es la misma que arma el digest por mail. Lo único que
 * pone la pantalla es el NOMBRE de cada tramo, en el idioma de quien mira.
 */
export function DueSoonView({
  title,
  description,
  lockedAssigneeId,
  defaultGroupBy,
}: DueSoonViewProps) {
  const t = useT()
  const { filters, queryFilters, isFiltered, setFilter, clearFilters } = useDueSoonFilters({
    ...(defaultGroupBy ? { groupBy: defaultGroupBy } : {}),
  })
  const [openTask, setOpenTask] = useState<Task | null>(null)

  const groupOptions = useMemo<readonly SegmentedOption<GroupBy>[]>(
    () => [
      { value: 'persona', label: t('dueSoon.filter.groupByPerson') },
      { value: 'proyecto', label: t('dueSoon.filter.groupByProject') },
    ],
    [t],
  )

  // El label es un número ("14 d"): el `title` es lo que lee un lector de
  // pantalla, y por eso va con formas plurales y no pegado a mano.
  const horizonOptions = useMemo<readonly SegmentedOption<`${Horizon}`>[]>(
    () =>
      HORIZONS.map((days) => ({
        value: String(days) as `${Horizon}`,
        label: t('dueSoon.filter.horizonOption', { dias: days }),
        title: t('dueSoon.filter.horizonTitle', { count: days }),
      })),
    [t],
  )

  const effective = lockedAssigneeId
    ? { ...queryFilters, responsable: lockedAssigneeId }
    : queryFilters

  const query = useDueSoonBuckets(effective)
  const stages = useTaskStages()
  const update = useUpdateTask()
  // Completar NO es un PATCH: son `POST /tasks/{id}/complete` y `/reopen`.
  const complete = useCompleteTask()
  const move = useMoveTask()
  const remove = useDeleteTask()

  const groupBy = filters.groupBy

  const buckets = useMemo(() => query.data ?? [], [query.data])
  const tasks = useMemo(() => buckets.flatMap((bucket) => bucket.tasks), [buckets])
  const selected = openTask ? (tasks.find((task) => task.id === openTask.id) ?? openTask) : null

  return (
    <div className="flex flex-col">
      <PageHeader title={title} description={description} />

      <div className="hairline-b sticky top-0 z-10 flex flex-wrap items-center gap-2 bg-surface-1 px-4 py-2">
        <Segmented
          label={t('dueSoon.filter.horizonLabel')}
          value={String(filters.horizon) as `${Horizon}`}
          options={horizonOptions}
          onChange={(value) => setFilter('horizon', Number(value) as Horizon)}
        />

        {/*
          Agrupar "por persona" en `/me/tasks` produce **un solo grupo**: todas
          las tareas son de la misma persona. El conmutador estaba igual, y un
          control que ofrece dos opciones donde una no cambia nada le hace
          perder un clic a cualquiera que lo pruebe (QA funcional 2.5).

          Se esconde con la misma condición que el filtro de responsable, que ya
          se escondía por el mismo motivo.
        */}
        {lockedAssigneeId ? null : (
          <Segmented
            label={t('dueSoon.filter.groupByLabel')}
            value={groupBy}
            options={groupOptions}
            onChange={(value) => setFilter('groupBy', value)}
          />
        )}

        <CompanySwitcher value={filters.company} onChange={(id) => setFilter('company', id)} />

        {lockedAssigneeId ? null : (
          <AssigneePicker
            asFilter
            value={filters.assignee}
            onChange={(id) => setFilter('assignee', id)}
          />
        )}

        {/*
          Tres estados y no un botón de "solo vencidas": el complemento —lo que
          todavía está a tiempo— no se podía pedir, y es justo adonde lleva el
          KPI "Vencen esta semana". Con un toggle, llegar filtrado por
          `por-vencer` habría dejado un filtro activo que la barra no mostraba.
        */}
        <Segmented
          label={t('dueSoon.filter.state.label')}
          value={filters.state ?? 'todas'}
          options={[
            { value: 'todas', label: t('dueSoon.filter.state.all') },
            { value: 'por-vencer', label: t('dueSoon.filter.state.pending') },
            { value: 'vencidas', label: t('dueSoon.filter.state.overdue') },
          ]}
          onChange={(value) => setFilter('state', value === 'todas' ? null : value)}
        />

        {isFiltered ? (
          <Button variant="ghost" onClick={clearFilters}>
            {t('common.action.clearFilters')}
          </Button>
        ) : null}
      </div>

      <div className="flex flex-col gap-6 p-4">
        {query.isPending ? (
          <SkeletonCards count={6} />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : tasks.length === 0 && isFiltered ? (
          <NoResultsState onClearFilters={clearFilters} />
        ) : tasks.length === 0 ? (
          <EmptyState
            title={t('dueSoon.empty.title')}
            description={t('dueSoon.empty.description', { count: filters.horizon })}
          />
        ) : (
          buckets.map((bucket) => (
            <BucketSection
              key={bucket.key}
              bucketKey={bucket.key}
              label={bucket.label}
              count={bucket.tasks.length}
            >
              <div className="flex flex-col gap-4">
                {groupTasks(bucket.tasks, groupBy).map((group) => (
                  <DueSoonGroup
                    key={group.id}
                    group={group}
                    onOpen={setOpenTask}
                    onToggleDone={(task, done) => complete.mutate({ id: task.id, done })}
                  />
                ))}
              </div>
            </BucketSection>
          ))
        )}
      </div>

      <TaskDetailSheet
        task={selected}
        stages={stages.data ?? []}
        onClose={() => setOpenTask(null)}
        onPatch={(task, patch, optimistic) => update.mutate({ id: task.id, patch, optimistic })}
        onMoveToStage={(task, stage) =>
          move.mutate({ id: task.id, beforeId: null, afterId: null, stage })
        }
        onToggleDone={(current, done) => complete.mutate({ id: current.id, done })}
        onDelete={(task) => remove.mutate(task.id)}
      />
    </div>
  )
}
